import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { searchGuide } from "@/data/searchIndex";

const TYPE_COLORS = {
  "Órgão": "#A63A2B",
  Elemento: "#2C4A3E",
  "Emoção": "#1E3A5F",
  Sintoma: "#B87333",
  "Chás": "#3D6652",
  Alimentos: "#B87333",
  Sabor: "#5A6B7C",
  "Hábitos": "#2C4A3E",
  "Relógio": "#C5A059",
};

export default function SearchModal({ open, onClose }) {
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const results = searchGuide(query);

  useEffect(() => {
    if (open) {
      setQuery("");
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const go = (to) => {
    onClose();
    navigate(to);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center p-4 pt-[10vh]" data-testid="search-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl rounded-2xl border border-gold/30 bg-popover shadow-2xl overflow-hidden animate-fade-up">
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <Search className="h-5 w-5 text-muted-foreground" strokeWidth={1.75} />
          <input
            ref={inputRef}
            data-testid="search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar no guia: respiração, fígado, insônia, chá…"
            className="flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground/60"
          />
          <button data-testid="search-close" onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="max-h-[55vh] overflow-y-auto p-2">
          {query && results.length === 0 && (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground">
              Nada encontrado no guia para “{query}”. A busca cobre apenas o conteúdo deste material.
            </p>
          )}
          {!query && (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground">
              Pesquise órgãos, sintomas, emoções, elementos, sabores, alimentos, chás e hábitos citados no guia.
            </p>
          )}
          {results.map((r, i) => (
            <button
              key={i}
              data-testid={`search-result-${i}`}
              onClick={() => go(r.to)}
              className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left transition-colors hover:bg-secondary"
            >
              <span
                className="rounded-md px-2 py-0.5 text-[11px] font-semibold text-white shrink-0"
                style={{ backgroundColor: TYPE_COLORS[r.type] || "#5A6B7C" }}
              >
                {r.type}
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium text-foreground">{r.title}</span>
                {r.subtitle && <span className="block truncate text-xs text-muted-foreground">{r.subtitle}</span>}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
